import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  PageHeader,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import {
  TrxSweepEnqueueResult,
  TrxSweepProgress,
  TrxSweepProvider,
  TrxSweepRow,
} from '../../domain/usdt-providers/trxSweepProvider';
import { GlobalSetting, GlobalSettingProvider, SweepDestOption } from '../../domain/usdt-providers/globalSettingProvider';
import { Environment } from '../../core/configs/services';
import _module from '../configs/module';
import { WRITE_SYSTEM_SETTING } from '../configs/scopes';
import { normalizeOtp } from '../../core/utils/otp';

const { Text } = Typography;

const POLL_MS = 3000;

const errMsg = (e: any) =>
  e?.response?.data?.displayMessage || e?.response?.data?.message || e?.message || '失敗';

function tronscanTxUrl(txId: string): string {
  const env = Environment() ?? 'develop';
  const base = env === 'prod' || env === 'stg' ? 'https://tronscan.org/#/transaction/' : 'https://shasta.tronscan.org/#/transaction/';
  return base + txId;
}

function tronscanAddressUrl(address: string): string {
  const env = Environment() ?? 'develop';
  const base = env === 'prod' || env === 'stg' ? 'https://tronscan.org/#/address/' : 'https://shasta.tronscan.org/#/address/';
  return base + address;
}

const num = (v?: string | number | null) =>
  v !== null && v !== undefined && v !== '' ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '-';

const statusMap: Record<string, { color: string; text: string }> = {
  pending: { color: 'default', text: '排隊中' },
  running: { color: 'processing', text: '回收中' },
  done: { color: 'success', text: '已回收' },
  skipped: { color: 'default', text: '跳過' },
  failed: { color: 'error', text: '失敗' },
};

/**
 * 一次性 TRX 回收工具的後台面板。
 *
 * 結構/2FA modal/進度輪詢沿用 AbandonWalletPage + RetireActions 的「清空鏈上餘額」
 * 那一支 —— 同樣是「觸發一批背景動作 + 之後輪詢看結果」的形狀,差別只在這裡是
 * 後端自己掃四類錢包(不必前端先勾選地址)。
 *
 * enqueue 掛了 MchBOValidateGuard(通用防竄改簽名,跟商戶無關,每個會動錢/寫狀態
 * 的 sibling 端點都有),body 必須帶 boSign,否則後端回 403 sign_invalidate ——
 * 用跟 AbandonWalletProvider 一樣的 BoSignHelper.generate(見 trxSweepProvider.ts),
 * 不重新發明簽名邏輯。
 */
const TrxSweepManager = (props: { title: string }) => {
  const [setting, setSetting] = useState<GlobalSetting | null>(null);
  const [settingLoading, setSettingLoading] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [enqueueLoading, setEnqueueLoading] = useState(false);
  const [enqueueErr, setEnqueueErr] = useState('');
  const [form] = Form.useForm();

  const [batchId, setBatchId] = useState<string | null>(null);
  const [enqueueResult, setEnqueueResult] = useState<TrxSweepEnqueueResult | null>(null);
  const [progress, setProgress] = useState<TrxSweepProgress | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const canWrite = _module.can([WRITE_SYSTEM_SETTING]);
  const thresholdSet = setting?.TRX_SWEEP_MIN !== undefined && setting?.TRX_SWEEP_MIN !== null && String(setting?.TRX_SWEEP_MIN).trim() !== '';
  // F1(一次性 TRX 回收)的有效目的地:優先 TRX_SWEEP_DEST,未設 fallback RETIRE_SWEEP_DEST(與後端一致)。
  const trxDestEffective = setting?.TRX_SWEEP_DEST || setting?.RETIRE_SWEEP_DEST || '';
  const destSet = !!trxDestEffective;
  const [thresholdInput, setThresholdInput] = useState<number | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [destOptions, setDestOptions] = useState<SweepDestOption[]>([]);
  const [savingDest, setSavingDest] = useState<string | null>(null);
  const destOptLabel = (o: SweepDestOption) =>
    `${o.label}(${o.address.slice(0, 6)}…${o.address.slice(-4)})`;

  const saveDest = (
    id: 'TRX_SWEEP_DEST' | 'RETIRE_SWEEP_DEST',
    address: string,
  ) => {
    const names: Record<string, string> = {
      TRX_SWEEP_DEST: '一次性 TRX 回收',
      RETIRE_SWEEP_DEST: '棄用錢包歸集',
    };
    const opt = destOptions.find((o) => o.address === address);
    Modal.confirm({
      title: `把「${names[id]}」目的地設為 ${opt?.label ?? address}?`,
      content: `之後這類回收會把錢掃到 ${address}(${opt?.label ?? '白名單地址'})。`,
      okText: '確認',
      cancelText: '取消',
      centered: true,
      onOk: async () => {
        setSavingDest(id);
        try {
          await GlobalSettingProvider.put({ [id]: address } as Partial<GlobalSetting>);
          message.success('目的地已更新');
          await loadSetting();
        } catch (e: any) {
          message.error(errMsg(e));
        } finally {
          setSavingDest(null);
        }
      },
    });
  };

  const loadSetting = async () => {
    setSettingLoading(true);
    try {
      const [res, opts] = await Promise.all([
        GlobalSettingProvider.get(),
        GlobalSettingProvider.getSweepDestOptions().catch(
          () => ({ data: [] as SweepDestOption[] } as any),
        ),
      ]);
      setSetting(res.data);
      setDestOptions(opts?.data ?? []);
    } catch (e: any) {
      message.error(errMsg(e));
    } finally {
      setSettingLoading(false);
    }
  };

  const fetchProgress = async (id: string) => {
    setProgressLoading(true);
    try {
      const r = await TrxSweepProvider.getProgress(id);
      setProgress(r.data);
    } catch (e: any) {
      message.error(errMsg(e));
    } finally {
      setProgressLoading(false);
    }
  };

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  // 沒有跑完(還有 pending 或 running)就繼續輪詢;全部到終態就停,省得一直打 API。
  useEffect(() => {
    if (!batchId) return;
    fetchProgress(batchId);
    stopPolling();
    pollRef.current = setInterval(() => fetchProgress(batchId), POLL_MS);
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId]);

  useEffect(() => {
    if (!progress) return;
    if (progress.scanning === 0 && progress.pending === 0 && progress.running === 0) {
      stopPolling();
    }
  }, [progress]);

  useEffect(() => {
    loadSetting();
    return stopPolling;
    // eslint-disable-next-line
  }, []);

  // 門檻編輯框跟著讀到的設定同步(存檔後 loadSetting 會刷新);空值 → null(代表停用)
  useEffect(() => {
    const raw = setting?.TRX_SWEEP_MIN;
    const n = raw === undefined || raw === null || String(raw).trim() === '' ? null : Number(raw);
    setThresholdInput(typeof n === 'number' && Number.isFinite(n) ? n : null);
  }, [setting]);

  const openConfirm = () => {
    setEnqueueErr('');
    form.resetFields();
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    form.resetFields();
    setEnqueueErr('');
    setConfirmOpen(false);
  };

  const submitEnqueue = async () => {
    const v = await form.validateFields();
    setEnqueueLoading(true);
    try {
      const r = await TrxSweepProvider.enqueue({ code: normalizeOtp(v.code) });
      message.success(`已排入 ${r.data.candidates} 顆候選,背景掃描中…`);
      setEnqueueResult(r.data);
      setBatchId(r.data.batchId);
      closeConfirm();
    } catch (e: any) {
      setEnqueueErr(errMsg(e));
    } finally {
      setEnqueueLoading(false);
    }
  };

  const saveThreshold = async () => {
    // 空 = 停用。關鍵:絕不能把「清空」變成 0 —— 0 會掃光所有 >0 TRX 的錢包(跟停用相反)。
    if (thresholdInput === null || thresholdInput === undefined) {
      Modal.confirm({
        title: '清空門檻 = 停用一次性 TRX 回收工具',
        content: '確定停用?停用後「掃描並排入」會被鎖住(已排入的批次不受影響)。',
        okText: '確定停用',
        cancelText: '取消',
        onOk: async () => {
          setSavingThreshold(true);
          try {
            await GlobalSettingProvider.put({ TRX_SWEEP_MIN: '' });
            message.success('門檻已清空 —— 工具已停用');
            await loadSetting();
          } catch (e: any) {
            message.error(errMsg(e));
          } finally {
            setSavingThreshold(false);
          }
        },
      });
      return;
    }
    if (!(thresholdInput > 0)) {
      message.error('門檻必須大於 0(0 會掃光所有有 TRX 的錢包);要停用請把欄位清空');
      return;
    }
    Modal.confirm({
      title: `把回收門檻設為 ${thresholdInput} TRX?`,
      content: `這會武裝一次性 TRX 回收工具:之後按「掃描並排入」,凡鏈上 TRX 高於 ${thresholdInput} 的用戶/商戶/棄用/已停用出款錢包都會被轉到目的地 ${trxDestEffective || '(未設定)'}。`,
      okText: '確定設定',
      cancelText: '取消',
      onOk: async () => {
        setSavingThreshold(true);
        try {
          await GlobalSettingProvider.put({ TRX_SWEEP_MIN: String(thresholdInput) });
          message.success(`門檻已設為 ${thresholdInput} TRX`);
          await loadSetting();
        } catch (e: any) {
          message.error(errMsg(e));
        } finally {
          setSavingThreshold(false);
        }
      },
    });
  };

  const columns = [
    { title: '錢包地址', dataIndex: 'walletAddress', align: 'center' as const, ellipsis: true },
    { title: '來源', dataIndex: 'sourceType', align: 'center' as const },
    {
      title: '排入當下鏈上 TRX',
      dataIndex: 'chainTrx',
      align: 'right' as const,
      render: (v: any) => num(v),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      align: 'center' as const,
      render: (v: string, row: TrxSweepRow) => {
        const m = statusMap[v] ?? { color: 'default', text: v };
        return (
          <Tooltip title={row.errorMsg || ''}>
            <Tag color={m.color}>{m.text}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '已回收 TRX',
      dataIndex: 'sweptAmount',
      align: 'right' as const,
      render: (v: any) => num(v),
    },
    {
      title: '交易',
      dataIndex: 'txId',
      align: 'center' as const,
      render: (v: string | null) =>
        v ? (
          // eslint-disable-next-line react/jsx-no-target-blank
          <a href={tronscanTxUrl(v)} target="_blank" rel="noreferrer">
            {v.slice(0, 8)}...
          </a>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        ),
    },
    {
      title: '錯誤訊息',
      dataIndex: 'errorMsg',
      align: 'left' as const,
      ellipsis: true,
      render: (v: string | null) => v || '-',
    },
  ];

  return (
    <>
      <PageHeader title={props.title} subTitle="" extra={<></>} />
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title="門檻與目的地" loading={settingLoading}>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="回收門檻 TRX_SWEEP_MIN">
              {canWrite ? (
                <Space wrap>
                  <InputNumber
                    min={0}
                    value={thresholdInput as number}
                    onChange={(v) => setThresholdInput(v === undefined || v === null ? null : (v as number))}
                    placeholder="清空 = 停用"
                    style={{ width: 160 }}
                  />
                  <Text type="secondary">TRX</Text>
                  <Button type="primary" size="small" loading={savingThreshold} onClick={saveThreshold}>
                    儲存門檻
                  </Button>
                  {thresholdSet ? (
                    <Tag color="success">啟用中(&gt; {num(setting?.TRX_SWEEP_MIN)} TRX)</Tag>
                  ) : (
                    <Tag color="error">未設定 —— 工具已停用</Tag>
                  )}
                </Space>
              ) : thresholdSet ? (
                <b>{num(setting?.TRX_SWEEP_MIN)} TRX</b>
              ) : (
                <Tag color="error">未設定 —— 工具已停用</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="一次性 TRX 回收目的地 TRX_SWEEP_DEST">
              {canWrite ? (
                <Space wrap>
                  <Select
                    style={{ width: 340 }}
                    placeholder="選擇目的地(僅限白名單)"
                    loading={savingDest === 'TRX_SWEEP_DEST'}
                    value={setting?.TRX_SWEEP_DEST || undefined}
                    onChange={(v) => saveDest('TRX_SWEEP_DEST', v)}
                    options={destOptions.map((o) => ({ value: o.address, label: destOptLabel(o) }))}
                  />
                  {setting?.TRX_SWEEP_DEST ? (
                    <a href={tronscanAddressUrl(setting.TRX_SWEEP_DEST)} target="_blank" rel="noreferrer">看鏈</a>
                  ) : (
                    <Tag color="warning">未設 —— 沿用棄用歸集目的地</Tag>
                  )}
                </Space>
              ) : (
                <Text code>{setting?.TRX_SWEEP_DEST || '(未設,沿用 RETIRE_SWEEP_DEST)'}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="棄用錢包歸集目的地 RETIRE_SWEEP_DEST">
              {canWrite ? (
                <Space wrap>
                  <Select
                    style={{ width: 340 }}
                    placeholder="選擇目的地(僅限白名單)"
                    loading={savingDest === 'RETIRE_SWEEP_DEST'}
                    value={setting?.RETIRE_SWEEP_DEST || undefined}
                    onChange={(v) => saveDest('RETIRE_SWEEP_DEST', v)}
                    options={destOptions.map((o) => ({ value: o.address, label: destOptLabel(o) }))}
                  />
                  {setting?.RETIRE_SWEEP_DEST ? (
                    <a href={tronscanAddressUrl(setting.RETIRE_SWEEP_DEST)} target="_blank" rel="noreferrer">看鏈</a>
                  ) : (
                    <Tag color="error">未設 —— 棄用清空已停用</Tag>
                  )}
                </Space>
              ) : (
                <Text code>{setting?.RETIRE_SWEEP_DEST || '(未設)'}</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
          <div style={{ marginTop: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              門檻可在此調整(清空 = 停用;不接受 0)。目的地只能從白名單下拉選(RENTAL 能量站 / 老闆外部地址)——
              限定白名單是為了:被盜用的後台帳號無法把回收的錢改送到任意地址。
              一次性 TRX 回收未設 TRX_SWEEP_DEST 時,自動沿用棄用歸集目的地 RETIRE_SWEEP_DEST。
            </Text>
          </div>
        </Card>

        <Card
          title="掃描並排入"
          extra={
            <Button
              type="primary"
              danger
              disabled={!canWrite || !thresholdSet || !destSet}
              onClick={openConfirm}
            >
              掃描並排入
            </Button>
          }
        >
          <Alert
            type="warning"
            showIcon
            message="一次性工具"
            description={
              <span>
                依序掃描四類錢包(用戶/商戶/棄用/已停用出款)的鏈上 TRX,凡高於門檻者
                排入背景佇列,由 cron 一次清一顆、轉到上方目的地(保留少量底避免歸零)。
                運營錢包中只有「已停用的出款錢包」會被排入,啟用中的出款錢包與所有能量站錢包一律不動。
              </span>
            }
          />
          {enqueueResult && (
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic title="本批 Batch ID" value={enqueueResult.batchId} />
              </Col>
              <Col span={12}>
                <Statistic title="候選(背景掃描中)" value={enqueueResult.candidates} />
              </Col>
            </Row>
          )}
        </Card>

        {batchId && (
          <Card
            title={`進度 —— batchId: ${batchId}`}
            extra={
              <Button size="small" loading={progressLoading} onClick={() => fetchProgress(batchId)}>
                重新整理
              </Button>
            }
          >
            {progress && (
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={4}>
                  <Statistic title="掃描中" value={progress.scanning} valueStyle={{ color: '#faad14' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="排隊中" value={progress.pending} />
                </Col>
                <Col span={4}>
                  <Statistic title="處理中" value={progress.running} valueStyle={{ color: '#1890ff' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="已完成" value={progress.done} valueStyle={{ color: '#3f8600' }} />
                </Col>
                <Col span={4}>
                  <Statistic title="跳過" value={progress.skipped} />
                </Col>
                <Col span={4}>
                  <Statistic title="失敗" value={progress.failed} valueStyle={{ color: '#cf1322' }} />
                </Col>
              </Row>
            )}
            <Table
              rowKey="walletAddress"
              size="small"
              loading={progressLoading}
              dataSource={progress?.rows || []}
              columns={columns as any}
              pagination={{ pageSize: 20 }}
            />
          </Card>
        )}
      </Space>

      <Modal
        title="掃描並排入 —— 一次性 TRX 回收"
        visible={confirmOpen}
        onCancel={closeConfirm}
        onOk={submitEnqueue}
        okText="確認執行"
        okButtonProps={{ danger: true, loading: enqueueLoading }}
        destroyOnClose
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message="這個動作不可逆"
            description={
              <span>
                會依門檻 <Text code>{num(setting?.TRX_SWEEP_MIN)} TRX</Text> 掃描並排入回收佇列,
                過門檻的錢包稍後會由背景 cron 逐顆把 TRX 轉到 <Text code>{setting?.RETIRE_SWEEP_DEST}</Text>。
              </span>
            }
          />
          <Form form={form} layout="vertical">
            <Form.Item
              name="code"
              label="驗證碼"
              rules={[{ required: true, pattern: /^\d{6}$/, message: '請輸入驗證碼' }]}
            >
              <Input
                maxLength={6}
                prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="Google Authenticator"
              />
            </Form.Item>
          </Form>
          {enqueueErr && <Alert type="error" showIcon message={enqueueErr} />}
        </Space>
      </Modal>
    </>
  );
};

export default TrxSweepManager;
